import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import toast from 'react-hot-toast';
import api from '../../utils/api';
import PropertyMap from '../../components/shared/PropertyMap';
import Topbar from '../../components/shared/Topbar';
import LandlordSidebar from '../../components/landlord/LandlordSidebar';
import LegalFooter from '../../components/shared/LegalFooter';
import '../../styles/global.css';

const fmtDate = (d) => d ? new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '—';
const fmt = (n) => parseFloat(n || 0).toFixed(2);

export default function LandlordProperty() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [property, setProperty] = useState(null);
  const [units, setUnits] = useState([]);
  const [loading, setLoading] = useState(true);
  const [preApprovals, setPreApprovals] = useState([]);
  const [showAddPA, setShowAddPA] = useState(false);
  const [paForm, setPaForm] = useState({ unit_number: '', first_name: '', last_name: '', email: '', phone: '', move_in_date: '' });
  const [savingPA, setSavingPA] = useState(false);
  const [showInvite, setShowInvite] = useState(false);
  const [inviting, setInviting] = useState(false);
  const { register, handleSubmit, reset, formState: { errors } } = useForm();

  const load = () => {
    api.get(`/landlord/properties/${id}/units`)
      .then(res => {
        setProperty(res.data.property);
        setUnits(res.data.units);
      })
      .catch(() => toast.error('Could not load property'))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    load();
    api.get('/pre-approvals?property_id=' + id)
      .then(res => setPreApprovals(res.data.pre_approvals || []))
      .catch(() => {});
  }, [id]);

  const onInvite = async (data) => {
    setInviting(true);
    try {
      await api.post('/landlord/invite', { property_id: id, ...data });
      toast.success(`Invite sent to ${data.tenant_email}`);
      reset();
      setShowInvite(false);
    } catch (err) {
      toast.error(err.response?.data?.error || 'Could not send invite');
    } finally {
      setInviting(false);
    }
  };

  const signupUrl = `${window.location.origin}/signup/${property?.slug}`;
  const copyLink = () => { navigator.clipboard.writeText(signupUrl); toast.success('Link copied!'); };

  if (loading) return <div className="loading-screen"><div className="spinner" /></div>;

  const active = units.filter(u => u.status === 'active').length;
  const vacant = units.filter(u => u.status === 'vacant').length;
  const adoption = property?.unit_count ? Math.round((active / property.unit_count) * 100) : 0;

  return (
    <div className="app-layout">
      <Topbar role="landlord" />
      <div className="dashboard-layout">
        <LandlordSidebar />
        <main id="main-content" className="main-content">
          {/* Breadcrumb */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, color: 'var(--text-sub)', marginBottom: 16 }}>
            <span style={{ cursor: 'pointer', color: 'var(--navy)' }} onClick={() => navigate('/landlord/dashboard')}>Portfolio</span>
            <span>›</span>
            <span style={{ fontWeight: 600, color: 'var(--navy)' }}>{property?.name}</span>
          </div>

          {/* Header */}
          <div className="flex-between" style={{ marginBottom: 20 }}>
            <div>
              <div className="page-title">{property?.name}</div>
              <div className="page-sub" style={{ margin: 0 }}>{property?.address} · {property?.city}, {property?.state} · {property?.unit_count} units</div>
            </div>
            <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
              <button className="btn btn-outline btn-sm" onClick={copyLink}>📋 Copy signup link</button>
              <button className="btn btn-primary btn-sm" onClick={() => setShowInvite(!showInvite)}>
                {showInvite ? 'Cancel' : '+ Enroll tenant'}
              </button>
            </div>
          </div>

          {/* Enroll tenant form */}
          {showInvite && (
            <div className="card" style={{ marginBottom: 20 }}>
              <div className="card-header"><div className="card-title">Send tenant invite</div></div>
              <div className="card-body">
                <form onSubmit={handleSubmit(onInvite)}>
                  <div className="two-col">
                    <div className="form-group">
                      <label className="form-label">Tenant email</label>
                      <input type="email" className={`form-input${errors.tenant_email ? ' error' : ''}`}
                        placeholder="tenant@email.com" {...register('tenant_email', { required: true })} />
                      {errors.tenant_email && <div className="form-error">Required</div>}
                    </div>
                    <div className="form-group">
                      <label className="form-label">Unit number</label>
                      <select className={`form-input${errors.unit_number ? ' error' : ''}`} {...register('unit_number', { required: true })}>
                        <option value="">Select unit</option>
                        {units.filter(u => u.status !== 'active').map(u => (
                          <option key={u.id} value={u.unit_number}>{u.unit_number}</option>
                        ))}
                      </select>
                      {errors.unit_number && <div className="form-error">Required</div>}
                    </div>
                  </div>
                  <button type="submit" className="btn btn-primary" disabled={inviting}>
                    {inviting ? 'Sending...' : 'Send Invite Email'}
                  </button>
                </form>
              </div>
            </div>
          )}

          {/* Metrics */}
          <div className="metric-grid">
            <div className="metric-card"><div className="metric-label">Total units</div><div className="metric-value">{property?.unit_count}</div></div>
            <div className="metric-card"><div className="metric-label">Active tenants</div><div className="metric-value accent">{active}</div></div>
            <div className="metric-card"><div className="metric-label">Vacant units</div><div className="metric-value" style={{ color: '#b85c00' }}>{vacant}</div></div>
            <div className="metric-card"><div className="metric-label">Adoption rate</div><div className="metric-value green">{adoption}%</div></div>
          </div>

          {/* Signup link info box */}
          <div style={{ background: 'var(--light-bg)', border: '1px solid var(--border)', borderRadius: 10, padding: '12px 16px', marginBottom: 20, display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 10 }}>
            <div>
              <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-sub)', marginBottom: 2 }}>Tenant signup link for this property</div>
              <div style={{ fontSize: 13, color: 'var(--navy)', fontFamily: 'monospace' }}>{signupUrl}</div>
            </div>
            <button className="btn btn-outline btn-sm" onClick={copyLink}>Copy</button>
          </div>

          {/* Unit roster */}
          <div className="card">
            <div className="card-header">
              <div className="card-title">Unit roster</div>
              <button className="btn btn-outline btn-sm">Export CSV</button>
            </div>
            <div className="table-wrap">
              <table>
                <thead>
                  <tr><th>Unit</th><th>Tenant</th><th>Plan</th><th>Monthly</th><th>Status</th><th>Enrolled</th></tr>
                </thead>
                <tbody>
                  {units.map(u => (
                    <tr key={u.id}>
                      <td style={{ fontWeight: 700 }}>{u.unit_number}</td>
                      <td>{u.first_name ? `${u.first_name} ${u.last_name}` : '—'}</td>
                      <td>{u.plan_name || '—'}</td>
                      <td style={{ fontWeight: 700 }}>{u.bulknet_price ? `$${fmt(u.bulknet_price)}` : '—'}</td>
                      <td>
                        <span className={`badge badge-${u.status === 'active' ? 'green' : u.status === 'pending' ? 'orange' : 'gray'}`}>
                          {u.status}
                        </span>
                      </td>
                      <td>{fmtDate(u.enrolled_at)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          {/* Pre-Approvals */}
          <div className="card" style={{ marginTop: 20 }}>
            <div className="card-header">
              <div className="card-title">Expected Move-Ins ({preApprovals.filter(p => p.status === 'pending').length})</div>
              <button className="btn btn-primary btn-sm" onClick={() => setShowAddPA(!showAddPA)}>
                {showAddPA ? 'Cancel' : '+ Add Move-In'}
              </button>
            </div>
            <div className="card-body" style={{ padding: showAddPA ? 20 : 0 }}>
              {showAddPA && (
                <div style={{ marginBottom: 16, paddingBottom: 16, borderBottom: '1px solid var(--border)' }}>
                  <div className="two-col" style={{ marginBottom: 12 }}>
                    <div className="form-group" style={{ marginBottom: 0 }}>
                      <label className="form-label">Unit *</label>
                      <input className="form-input" value={paForm.unit_number} onChange={e => setPaForm({ ...paForm, unit_number: e.target.value })} placeholder="e.g. 4B" />
                    </div>
                    <div className="form-group" style={{ marginBottom: 0 }}>
                      <label className="form-label">Move-In Date</label>
                      <input type="date" className="form-input" value={paForm.move_in_date} onChange={e => setPaForm({ ...paForm, move_in_date: e.target.value })} />
                    </div>
                  </div>
                  <div className="two-col" style={{ marginBottom: 12 }}>
                    <div className="form-group" style={{ marginBottom: 0 }}>
                      <label className="form-label">First Name</label>
                      <input className="form-input" value={paForm.first_name} onChange={e => setPaForm({ ...paForm, first_name: e.target.value })} />
                    </div>
                    <div className="form-group" style={{ marginBottom: 0 }}>
                      <label className="form-label">Last Name</label>
                      <input className="form-input" value={paForm.last_name} onChange={e => setPaForm({ ...paForm, last_name: e.target.value })} />
                    </div>
                  </div>
                  <div className="two-col" style={{ marginBottom: 12 }}>
                    <div className="form-group" style={{ marginBottom: 0 }}>
                      <label className="form-label">Email</label>
                      <input type="email" className="form-input" value={paForm.email} onChange={e => setPaForm({ ...paForm, email: e.target.value })} />
                    </div>
                    <div className="form-group" style={{ marginBottom: 0 }}>
                      <label className="form-label">Phone</label>
                      <input className="form-input" value={paForm.phone} onChange={e => setPaForm({ ...paForm, phone: e.target.value })} />
                    </div>
                  </div>
                  <button className="btn btn-primary btn-sm" disabled={!paForm.unit_number || savingPA} onClick={async () => {
                    setSavingPA(true);
                    try {
                      await api.post('/pre-approvals/landlord', { ...paForm, property_id: id });
                      const res = await api.get('/pre-approvals?property_id=' + id);
                      setPreApprovals(res.data.pre_approvals || []);
                      setPaForm({ unit_number: '', first_name: '', last_name: '', email: '', phone: '', move_in_date: '' });
                      setShowAddPA(false);
                      toast.success('Pre-approval added');
                    } catch (err) { toast.error(err.response?.data?.error || 'Could not save'); }
                    finally { setSavingPA(false); }
                  }}>{savingPA ? 'Saving...' : 'Save'}</button>
                </div>
              )}

              <div className="table-wrap">
                <table>
                  <thead><tr><th>Unit</th><th>Name</th><th>Move-In</th><th>Status</th></tr></thead>
                  <tbody>
                    {preApprovals.length === 0 && (
                      <tr><td colSpan={4} style={{ textAlign: 'center', color: 'var(--text-sub)' }}>No expected move-ins</td></tr>
                    )}
                    {preApprovals.map(pa => (
                      <tr key={pa.id}>
                        <td style={{ fontWeight: 700 }}>{pa.unit_number}</td>
                        <td>{pa.first_name} {pa.last_name}</td>
                        <td style={{ fontSize: 12 }}>{pa.move_in_date ? fmtDate(pa.move_in_date) : '—'}</td>
                        <td>
                          <span className={`badge badge-${pa.status === 'activated' ? 'green' : pa.status === 'expired' ? 'red' : 'orange'}`}>{pa.status}</span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>

                <PropertyMap address={property?.address} city={property?.city} state={property?.state} zip={property?.zip} name={property?.name} />

</div>
          <LegalFooter />
        </main>
      </div>
    </div>
  );
}
